import React, { FC, Fragment, useEffect, useState } from 'react';
import { Box, Grid, Paper, TextField } from '@mui/material';
import { LabClient } from 'models/client-directory';
import { useTranslation } from 'react-i18next';
import { LocalClinicFormData } from 'models/forms';
import { useFormik } from 'formik';
import { emptyLocalCliicFormData } from './form-helper';
import { Address } from 'models/contact';
import AddressForm from 'components/shared/address/address-form.component';
import { LoadingButton } from '@mui/lab';
import { isValidEmail } from 'utils/validations';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useMutation } from '@apollo/client';
import { modifyClientContactSettingsTemplate } from 'apollo/templates/client-directory';
import OrganizationNumberInput from 'components/shared/organization-number/organization-number.component';
import { Country } from 'models/country';
import { countryFromCode } from 'utils/country-helper';

type OwnClientContactProps = {
    labClient: LabClient;
    onContactSaved?: () => void;
};
const OwnClientContact: FC<OwnClientContactProps> = ({ labClient, onContactSaved }: OwnClientContactProps) => {
    const { t: translate } = useTranslation(['organization', 'common', 'organization-numbers']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [currentCountry, setCountry] = useState<Country>();

    const [updateSettings, { loading }] = useMutation(modifyClientContactSettingsTemplate, {
        onCompleted: () => {
            if (onContactSaved) {
                onContactSaved();
            }
        },
        onError(error) {
            showBoundary(error);
        },
    });
    const validate = (formData: LocalClinicFormData) => {
        const errors: Record<string, string> = {};
        if (!formData.name) {
            errors.name = translate('organization:VALIDATION:CLINIC_NAME');
        }
        if (formData.email && !isValidEmail(formData.email)) {
            errors.email = translate('organization:VALIDATION:INVALID_EMAIL');
        }
        if (!formData.address) {
            errors.address = 'ADDRESS_IS_REQUIRED';
        }
        return errors;
    };

    const formik = useFormik<LocalClinicFormData>({
        initialValues: emptyLocalCliicFormData(),
        validate,
        onSubmit: async (formData) => {
            updateSettings({
                variables: {
                    clientId: labClient.customerId,
                    organizationNumber: formData.organizationNumber,
                    name: formData.name,
                    contact: {
                        communication: {
                            email: formData.email,
                            phone: formData.phone,
                            site: formData.site,
                        },
                        address: formData.address,
                    },
                },
            });
        },
    });

    useEffect(() => {
        formik.setValues({
            name: labClient.customer?.name || '',
            organizationNumber: labClient.customer.organizationNumber || '',
            email: labClient.customer.contact?.communication?.email || '',
            phone: labClient.customer.contact?.communication?.phone || '',
            site: labClient.customer.contact?.communication?.site || '',
            address: labClient.customer.contact?.address,
        });
        if (labClient.customer?.contact?.address?.country) {
            setCountry(countryFromCode(labClient.customer.contact.address.country));
        }
    }, [labClient]);

    const handleAddressChange = (address: Address | undefined) => {
        formik.setFieldValue('address', address);
        setCountry(countryFromCode(address?.country || 'SE'));
    };

    function handleOrganizationNumberChange(value: string): void {
        formik.setFieldValue('organizationNumber', value);
        setTimeout(() => {
            formik.validateForm();
        }, 0);
    }

    return (
        <Fragment>
            <form onSubmit={formik.handleSubmit}>
                <Box sx={{ maxWidth: 900 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label={translate('organization:ORGANIZATION_NAME')}
                                required
                                fullWidth
                                helperText={formik.touched.name && formik.errors.name}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                {...formik.getFieldProps('name')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {currentCountry ? (
                                <OrganizationNumberInput
                                    label={translate(
                                        `organization-numbers:${currentCountry.alpha2Code || 'SE'}_ORGANIZATION_NUMBER`,
                                    )}
                                    country={currentCountry}
                                    disabled={!currentCountry?.alpha2Code}
                                    fullWidth
                                    onValueChange={handleOrganizationNumberChange}
                                    helperText={formik.touched.organizationNumber && formik.errors.organizationNumber}
                                    error={
                                        formik.touched.organizationNumber && Boolean(formik.errors.organizationNumber)
                                    }
                                    onBlur={formik.getFieldProps('organizationNumber').onBlur}
                                    name={formik.getFieldProps('organizationNumber').name}
                                    value={formik.getFieldProps('organizationNumber').value}
                                />
                            ) : undefined}
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                label={translate('common:EMAIL')}
                                fullWidth
                                helperText={formik.touched.email && formik.errors.email}
                                error={formik.touched.email && Boolean(formik.errors.email)}
                                {...formik.getFieldProps('email')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField label={translate('common:PHONE')} fullWidth {...formik.getFieldProps('phone')} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label={translate('common:WEBSITE')}
                                fullWidth
                                {...formik.getFieldProps('site')}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <AddressForm onChange={handleAddressChange} address={formik.values.address} />
                        </Grid>
                    </Grid>
                    <Box mt={2}>
                        <LoadingButton disabled={!formik.isValid} variant="contained" type="submit" loading={loading}>
                            {translate('common:SAVE')}
                        </LoadingButton>
                    </Box>
                </Box>
            </form>
        </Fragment>
    );
};

export default OwnClientContact;
